import { StreamAction, StreamActionItem } from "@/types";
import { cn } from "@/utils/cn";
import { ChevronDown, ChevronUp, Search, Zap } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingIconRight from "../agent-steps/loading-icon";
import { ActionIcon } from "./action-icon";

const shimmerTextClass =
  "bg-[linear-gradient(90deg,var(--muted-foreground)_0%,var(--foreground)_50%,var(--muted-foreground)_100%)] bg-[length:200%_100%] bg-clip-text text-transparent animate-shimmer";

const springEntrance = {
  initial: { opacity: 0, y: 10, scale: 0.97 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { type: "spring" as const, stiffness: 400, damping: 28 },
};

const prettifyName = (name: string) => {
  const spaced = name.replace(/[_-]+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const getHostname = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return undefined;
  }
};

const ActionItemRow = ({
  item,
  index,
}: {
  item: StreamActionItem;
  index: number;
}) => {
  const { t } = useTranslation();
  const entrance = {
    initial: { opacity: 0, y: 6 },
    animate: { opacity: 1, y: 0 },
    transition: {
      type: "spring" as const,
      stiffness: 500,
      damping: 30,
      delay: index * 0.04,
    },
  };

  if (item.kind === "compaction") {
    return (
      <motion.div
        {...entrance}
        className="flex items-center gap-2 py-1 text-xs text-muted-foreground"
      >
        <Zap size={12} className="shrink-0 text-primary" />
        <span className="truncate">
          {t("playground.offloadedTokens", { tokens: item.detail ?? "" })}
          {item.label ? ` · ${item.label}` : ""}
        </span>
      </motion.div>
    );
  }

  const hostname = item.url ? getHostname(item.url) : undefined;

  return (
    <motion.div
      {...entrance}
      className="flex items-center gap-2 py-1 min-w-0"
    >
      <ActionIcon
        icon={item.icon || "lucide:search"}
        size={13}
        className="text-muted-foreground"
      />
      {item.url ? (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 min-w-0 group"
        >
          <span className="text-xs text-foreground truncate group-hover:underline">
            {item.label}
          </span>
          {hostname && (
            <span className="text-xs text-primary shrink-0">{hostname}</span>
          )}
        </a>
      ) : (
        <span className="text-xs text-foreground truncate">{item.label}</span>
      )}
    </motion.div>
  );
};

const ActionGroup = ({ action }: { action: StreamAction }) => {
  const isRunning = action.status === "running";
  const hasDetails = action.items.length > 0 || !!action.detail;
  const [userToggled, setUserToggled] = useState<boolean | null>(null);
  const isOpen = userToggled ?? false;
  // For web search, show the input (detail) when collapsed so multiple
  // searches stay distinguishable; the tool name shows once expanded.
  const headerLabel =
    action.name === "use_web_search" && !isOpen && action.detail
      ? action.detail
      : prettifyName(action.name);

  return (
    <motion.div layout {...springEntrance} className="min-w-0">
      <button
        type="button"
        disabled={!hasDetails}
        onClick={() => setUserToggled(!isOpen)}
        className={cn(
          "flex items-center gap-2 w-full min-w-0 py-1 px-1.5 text-start rounded-lg relative overflow-hidden transition-colors duration-300",
          hasDetails && "cursor-pointer",
          isRunning && "bg-primary/5",
        )}
      >
        {isRunning && (
          <div className="absolute [inset-inline-start:0rem] bg-[var(--card-background)] blur-[50px] w-[100px] h-[245px] animate-slide pointer-events-none" />
        )}
        <span
          className={cn(
            "w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-muted text-muted-foreground transition-colors duration-300",
            isRunning && "bg-primary/15 text-primary",
          )}
        >
          <ActionIcon icon={action.icon} size={13} />
        </span>
        <span
          className={cn(
            "text-sm truncate",
            isRunning ? cn("font-medium", shimmerTextClass) : "text-foreground",
          )}
        >
          {headerLabel}
        </span>
        <span className="ms-auto flex items-center gap-1 shrink-0">
          {isRunning && (
            <motion.span
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              className="flex items-center"
            >
              <LoadingIconRight />
            </motion.span>
          )}
          {hasDetails && (
            <span className="text-muted-foreground">
              {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </span>
          )}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && hasDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="ms-4 ps-4 border-s border-border flex flex-col">
              {action.detail && (
                <div className="flex items-center gap-2 py-1 min-w-0">
                  <Search
                    size={13}
                    className="shrink-0 text-muted-foreground"
                  />
                  <span className="text-xs text-muted-foreground truncate">
                    {action.detail}
                  </span>
                </div>
              )}
              {action.items.map((item, index) => (
                <ActionItemRow key={item.id} item={item} index={index} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const StreamActions = ({
  actions,
}: {
  actions?: Array<StreamAction>;
}) => {
  const { t } = useTranslation();
  const isRunning = !!actions?.some((action) => action.status === "running");
  const [userToggled, setUserToggled] = useState<boolean | null>(null);

  // reset the manual toggle when a new run starts
  useEffect(() => {
    if (isRunning) setUserToggled(null);
  }, [isRunning]);

  if (!actions || actions.length === 0) {
    return null;
  }

  const isExpanded = userToggled ?? isRunning;

  return (
    <motion.div layout className="flex flex-col min-w-0 px-1">
      <button
        type="button"
        onClick={() => setUserToggled(!isExpanded)}
        className="flex items-center gap-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer w-fit"
      >
        <span className="flex items-center gap-1.5">
          {actions.slice(0, 6).map((action) => (
            <motion.span
              key={action.id}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 25 }}
              className={cn(
                "w-6 h-6 rounded-lg bg-muted flex items-center justify-center shrink-0",
                action.status === "running" && "bg-primary/15 text-primary",
              )}
            >
              <ActionIcon icon={action.icon} size={13} />
            </motion.span>
          ))}
          {actions.length > 6 && (
            <span className="w-6 h-6 rounded-lg bg-muted flex items-center justify-center shrink-0 text-[10px] font-medium">
              +{actions.length - 6}
            </span>
          )}
        </span>
        <span className="font-medium">
          {t("playground.actionsCount", { count: actions.length })}
        </span>
        <span className="flex items-center gap-1">
          {isExpanded ? t("playground.less") : t("playground.show")}
          <motion.span
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="flex items-center"
          >
            <ChevronDown size={13} />
          </motion.span>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-0.5 pt-1">
              {actions.map((action) => (
                <ActionGroup key={action.id} action={action} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
